import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { exportData, downloadFile } from '../services/exportService';
import { useAuth } from '../context/AuthContext';
import { EmployeeStatus, LOCATIONS_OPTIONS, Position } from '../types';
import { getPositions } from '../services/positionsService';
import { extractErrorMessage, reportFormError } from '../utils/errorHandling';

const ALL_EXPORT_FIELDS = [
  { key: 'id', label: 'ID' },
  { key: 'fullNames', label: 'Full Names' },
  { key: 'surname', label: 'Surname' },
  { key: 'email', label: 'Email' },
  { key: 'contactNumber', label: 'Contact Number' },
  { key: 'dateOfBirth', label: 'Date of Birth' },
  { key: 'idNumber', label: 'ID Number' },
  { key: 'gender', label: 'Gender' },
  { key: 'nationality', label: 'Nationality' },
  { key: 'maritalStatus', label: 'Marital Status' },
  { key: 'residentialAddress', label: 'Residential Address' },
  { key: 'employmentStatus', label: 'Employment Status' },
  { key: 'positionAppliedFor', label: 'Position Applied For' },
  { key: 'previouslyEmployedByTrigon', label: 'Previously Employed by Trigon' },
  { key: 'status', label: 'Status' },
  { key: 'employeeNumber', label: 'Employee Number' },
  { key: 'jobGrade', label: 'Job Grade' },
  { key: 'jobTitle', label: 'Job Title' },
  { key: 'department', label: 'Department' },
  { key: 'createdAt', label: 'Created At' },
  { key: 'updatedAt', label: 'Updated At' }
];

const STATUS_OPTIONS: EmployeeStatus[] = [
  'draft',
  'new_submission',
  'candidate',
  'under_review',
  'requested_rework',
  'profile_verified',
  'available_for_hire',
  'hired',
  'current_employee',
  'past_employee',
  'terminated',
  'blacklisted',
  'rejected'
];

type ExportFormValues = {
  format: 'csv' | 'excel';
  fields: string[];
  filterStatus: string;
  filterDepartment: string;
  filterLocation: string;
  filterPosition: string;
};

const ExportPage: React.FC = () => {
  const { user } = useAuth();
  const [positions, setPositions] = useState<Position[]>([]);
  const { register, handleSubmit, watch, setValue } = useForm<ExportFormValues>({
    defaultValues: {
      format: 'csv',
      fields: ALL_EXPORT_FIELDS.map(field => field.key),
      filterStatus: '',
      filterDepartment: '',
      filterLocation: '',
      filterPosition: '',
    },
  });
  const [isLoading, setIsLoading] = useState(false);

  const selectedFields = watch('fields');

  useEffect(() => {
    const loadPositions = async () => {
      try {
        const positionsData = await getPositions();
        setPositions(positionsData);
      } catch (error) {
        console.error('Failed to load positions:', error);
      }
    };
    loadPositions();
  }, []);

  // Check permissions - only HR and Admin can export
  if (!user || !['hr_clerk', 'admin', 'viewer'].includes(user.role)) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Export Employee Data</h1>
        <div className="text-center py-8">
          <p className="text-gray-600">You do not have permission to export employee data.</p>
          <p className="text-sm text-gray-500 mt-2">Only HR Clerks and Administrators can access this feature.</p>
        </div>
      </div>
    );
  }

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setValue('fields', ALL_EXPORT_FIELDS.map(field => field.key));
    } else {
      setValue('fields', []);
    }
  };

  const onSubmit = async (data: ExportFormValues) => {
    setIsLoading(true);
    try {
      if (data.fields.length === 0) {
        const message = 'Please select at least one field to export.';
        toast.error(message);
        reportFormError({
          formName: 'Export Employees',
          message,
          category: 'validation',
          validationErrors: [{ field: 'fields', message: 'At least one field is required' }],
          tags: ['export'],
          userEmail: user?.email,
        });
        return;
      }

      const filters: Record<string, any> = {};
      if (data.filterStatus) filters.status = data.filterStatus;
      if (data.filterDepartment) filters.department = data.filterDepartment;
      if (data.filterLocation) filters.location = data.filterLocation;
      if (data.filterPosition) filters.position = data.filterPosition;

      const response = await exportData(data.format, data.fields, filters);
      downloadFile(response.content, response.fileName, response.mimeType);
      toast.success(`Data exported successfully! ${response.recordCount} records exported.`);
    } catch (error) {
      console.error('Export error:', error);
      const errorMessage = extractErrorMessage(error, 'Failed to export data.');
      toast.error(errorMessage);
      reportFormError({
        formName: 'Export Employees',
        error,
        message: errorMessage,
        tags: ['export'],
        userEmail: user?.email,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Export Employee Data</h1>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Export Format */}
        <div>
          <h3 className="text-lg font-medium mb-2">Export Format</h3>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input {...register('format')} type="radio" value="csv" className="mr-2" />
              CSV
            </label>
            <label className="flex items-center">
              <input {...register('format')} type="radio" value="excel" className="mr-2" />
              Excel (XLSX)
            </label>
          </div>
        </div>

        {/* Filters */}
        <div>
          <h3 className="text-lg font-medium mb-4">Filters (Optional)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select {...register('filterStatus')} className="p-2 border rounded w-full">
                <option value="">All Statuses</option>
                {STATUS_OPTIONS.map(status => (
                  <option key={status} value={status}>
                    {status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
              <select {...register('filterPosition')} className="p-2 border rounded w-full">
                <option value="">All Positions</option>
                {positions.filter(pos => pos.isActive).map(position => (
                  <option key={position.id} value={position.name}>{position.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <select {...register('filterLocation')} className="p-2 border rounded w-full">
                <option value="">All Locations</option>
                {LOCATIONS_OPTIONS.map(location => (
                  <option key={location} value={location}>{location}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
              <input 
                {...register('filterDepartment')} 
                placeholder="Filter by Department" 
                className="p-2 border rounded w-full" 
              />
            </div>
          </div>
        </div>

        {/* Fields */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium">Fields to Export</h3>
            <div className="flex items-center space-x-4">
              <label className="flex items-center text-sm">
                <input 
                  type="checkbox" 
                  onChange={handleSelectAll}
                  checked={selectedFields.length === ALL_EXPORT_FIELDS.length}
                  className="mr-2" 
                />
                Select All
              </label>
              <span className="text-sm text-gray-500">
                {selectedFields.length} of {ALL_EXPORT_FIELDS.length} selected
              </span>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {ALL_EXPORT_FIELDS.map(field => (
              <label key={field.key} className="flex items-center p-2 border rounded hover:bg-gray-50">
                <input 
                  {...register('fields')} 
                  type="checkbox" 
                  value={field.key} 
                  className="mr-3" 
                />
                <span className="text-sm">{field.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Export Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">Export Information</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• CSV format is recommended for compatibility with spreadsheet applications</li>
            <li>• Excel format preserves formatting and supports larger datasets</li>
            <li>• Exported files will include only the selected fields and filtered records</li>
            <li>• Large exports may take a few moments to process</li>
          </ul>
        </div>

        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {selectedFields.length > 0 && (
              <span>Ready to export {selectedFields.length} field(s)</span>
            )}
          </div>
          {user?.role !== 'viewer' ? (
            <button 
              type="submit" 
              className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2" 
              disabled={isLoading || selectedFields.length === 0}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Exporting...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                  </svg>
                  <span>Export Data</span>
                </>
              )}
            </button>
          ) : (
            <div className="px-8 py-3 bg-gray-100 text-gray-600 rounded-lg border border-gray-300 flex items-center space-x-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span>Export disabled (View-only access)</span>
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default ExportPage;
